import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import styles from "./PaymentModal.module.scss";

interface TreatmentOption {
  id: number;
  description: string;
  procedure_time_minutes: number;
  recovery_time_minutes: number;
  price: number;
  currency_code: string;
  is_anesthetic_include: boolean;
  is_aftercare_include: boolean;
  is_vat_include: boolean;
  product_id: number;
  hospital_name: string;
}

interface PaymentModalProps {
  onClose: () => void;
  hospitalId: string;
  address: string;
  subway: string;
  title: string;
  profileImage: string;
  options: TreatmentOption[];
  hospitalName: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ onClose, options, hospitalId, address, subway, title, profileImage, hospitalName }) => {
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<{ [key: number]: number | undefined }>({});
  const navigate = useNavigate();
  
  console.log(options);

  const toggleOption = (id: number) => {
    setSelectedOptions(prev => {
      const newSelectedOptions = { ...prev };
      
      if (newSelectedOptions[id]) {
        delete newSelectedOptions[id];
      } else {
        newSelectedOptions[id] = 1;
      }
      
      return newSelectedOptions;
    });
  };
  
  const changeQuantity = (e: React.MouseEvent<HTMLButtonElement>, id: number, delta: number) => {
    e.stopPropagation();
  
    setSelectedOptions(prev => {
      const currentQuantity = prev[id] || 1;
      const newQuantity = currentQuantity + delta;
  
      if (newQuantity < 1) return prev;
  
      return {
        ...prev,
        [id]: newQuantity,
      };
    });
  };
  
  const calculateTotalPrice = () => {
    return options.reduce((acc, option) => {
      if (selectedOptions[option.id]) {
        return acc + (option.price * (selectedOptions[option.id] || 1));
      }
      return acc;
    }, 0);
  };
  
  const handleSubmit = async () => {
    if (Object.keys(selectedOptions).length === 0) return;

    const currencyCode = options[0]?.currency_code || "USD";
    const totalPrice = calculateTotalPrice();
    
    const orderData = {
      currency_code: currencyCode,
      total_price: totalPrice,
      address,
      subway,
      title,
      hospital_id: Number(hospitalId),
      hospital_name: hospitalName,
      order_products: options
        .filter(option => selectedOptions[option.id])
        .map(option => ({
          hospital_id: Number(hospitalId),
          product_id: option.product_id,
          option_id: option.id,
          quantity: selectedOptions[option.id],
          title: option.description,
          price: option.price,
        })),
        profile_image: profileImage,
        created_at: new Date().toISOString(),
    };

    sessionStorage.setItem("currentOrder", JSON.stringify(orderData));
    navigate('/order');
  };
  
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>&times;</button>
        <h2>Payment Options</h2>
        <div className={styles.optionsContainer}>
          {options.map((option, index) => (
            <div
              key={option.id}
              className={`${styles.optionCard} ${selectedOptions[option.id] ? styles.selected : ""}`}
              onClick={() => toggleOption(option.id)}
            >
              <div className={styles.leftSection}>
                <div className={styles.optionHeader}>
                  {t('treatmentDetail.option')} {String(index + 1).padStart(2, '0')}.
                </div>
                <div className={styles.optionContent}>
                  <p className={styles.optionConetentHeader}>{option.description}</p>
                  <p className={styles.optionConetentText}>
                    {t('paymentModal.procedureTime', { time: option.procedure_time_minutes })}
                    <br />
                    {t('paymentModal.recoveryTime', { time: option.recovery_time_minutes })}
                  </p>
                </div>
                {selectedOptions[option.id] && (
                  <div className={styles.quantityControl}>
                    <button onClick={(e) => changeQuantity(e, option.id, -1)}>-</button>
                    <span>{selectedOptions[option.id] || 1}</span>
                    <button onClick={(e) => changeQuantity(e, option.id, 1)}>+</button>
                  </div>
                )}
              </div>
              <div className={styles.rightSection}>
                <span className={styles.additionalFees}>
                  {option.is_anesthetic_include ? t('paymentModal.feeIncluded') : t('paymentModal.feeUnincluded')}
                  <br />
                  {option.is_aftercare_include ? t('paymentModal.aftercareIncluded') : t('paymentModal.aftercareUnincluded')}
                </span>
                <div className={styles.priceDetails}>
                  {option.is_vat_include ? t('paymentModal.vatIncluded') : t('paymentModal.vatUnincluded')}
                </div>
                <div className={styles.price}>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: option.currency_code,
                    maximumFractionDigits: 0,
                  }).format(option.price)}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          className={styles.nextButton}
          disabled={Object.keys(selectedOptions).length === 0}
          onClick={handleSubmit}
        >
          {t('paymentModal.next')}
        </button>
      </div>
    </div>
  );
};

export default PaymentModal;
